import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import LegalBackToLogin from "../components/LegalBackToLogin";
import { ThemeContext } from "../context/ThemeContext";

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    background: themeColors.bg,
    color: themeColors.text,
    padding: "48px 18px",
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 28,
    boxShadow: themeColors.glow,
    padding: "34px clamp(20px, 4vw, 48px)",
    backdropFilter: "blur(18px)",
  },
  eyebrow: {
    color: themeColors.accent2,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 10,
  },
  title: {
    fontSize: "clamp(32px, 5vw, 52px)",
    lineHeight: 1.02,
    margin: "0 0 10px",
    color: themeColors.text,
  },
  date: {
    color: themeColors.mutedText,
    marginBottom: 28,
    fontSize: 15,
  },
  section: {
    marginTop: 28,
    paddingTop: 22,
    borderTop: `1px solid ${themeColors.border}`,
  },
  h2: {
    color: themeColors.accent,
    fontSize: 22,
    margin: "0 0 10px",
  },
  p: {
    color: themeColors.text,
    fontSize: 16,
    lineHeight: 1.75,
    margin: "8px 0",
  },
  list: {
    color: themeColors.text,
    lineHeight: 1.75,
    paddingLeft: 22,
  },
  nav: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 36,
    paddingTop: 24,
    borderTop: `1px solid ${themeColors.border}`,
  },
  link: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 800,
  },
  footer: {
    marginTop: 22,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
  },
});

export default function PrivacyPolicy() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  return (
    <main style={styles.page}>
      <article style={styles.card}>
        <div style={styles.eyebrow}>AVAAIO Legal</div>
        <h1 style={styles.title}>Privacy Policy</h1>
        <div style={styles.date}>
          Effective and Last Updated: July 13, 2026
        </div>

        <section style={styles.section}>
          <h2 style={styles.h2}>Overview</h2>
          <p style={styles.p}>
            This Privacy Policy explains how American Globalo, LLC
            collects, uses, processes, stores, shares, and protects
            information when people use AVAAIO™, Ava AI, our websites,
            applications, account services, and related features.
          </p>
          <p style={styles.p}>
            Different features collect different information. Camera,
            microphone, device-motion, memory, support, and billing
            features are optional or available only when enabled,
            permitted, or included with the user’s account plan.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Information We Collect</h2>
          <ul style={styles.list}>
            <li>
              Account information, including name, email address,
              authentication provider, verification status, role,
              subscription status, profile settings, and account state.
            </li>
            <li>
              Messages, prompts, replies, files, images, audio,
              transcripts, profile information, memories, and other
              content users choose to submit.
            </li>
            <li>
              Technical information such as device and browser type,
              user agent, session information, authentication records,
              IP-derived information, logs, diagnostics, security
              events, and feature-entitlement requests.
            </li>
            <li>
              Support information such as name, email, category,
              subject, message, and optional session context, including
              the page address and browser user agent.
            </li>
            <li>
              Subscription, transaction, purchase-validation, renewal,
              cancellation, and billing-status information received
              from Apple, Stripe, or another authorized billing
              provider. AVAAIO does not need to receive a user’s full
              payment-card number when payment is handled by a payment
              provider.
            </li>
            <li>
              A numeric AVAAIO rating submitted by a verified account.
              Aggregate rating statistics may be displayed publicly,
              but the current rating feature does not collect a public
              written review.
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Ava Vision and Camera Processing</h2>
          <p style={styles.p}>
            Ava Vision begins in the off position. When a user
            deliberately enables Ava Vision and grants camera
            permission, camera frames may be captured and transmitted
            to AVAAIO’s authenticated server endpoint for visual
            analysis.
          </p>
          <p style={styles.p}>
            Visual processing may identify objects, visible text,
            general scenes, hands, gestures, and whether a face-shaped
            region is present. Ava Vision is not facial recognition,
            does not determine a person’s identity, and does not use
            face-location information as biometric authentication or
            authorization.
          </p>
          <p style={styles.p}>
            The current system may hold up to ten recent derived
            visual-analysis records for an authenticated user in
            server memory to support short-term visual continuity.
            Derived records may include object results, scene
            descriptions, text results, gesture information, event
            information, timestamps, and limited face-location
            evidence. Authorized troubleshooting or diagnostic modes
            may create diagnostic visual output. Diagnostic tools and
            detailed visual telemetry are restricted to authorized
            administrators.
          </p>
          <p style={styles.p}>
            Users should not activate the camera where recording or
            image processing would violate another person’s privacy,
            property rights, workplace rules, or applicable law.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Microphone, Speech-to-Text, and Voice</h2>
          <p style={styles.p}>
            AVAAIO accesses the microphone only after the user enables
            the microphone and grants device or browser permission.
            Audio may be recorded for the purpose of converting speech
            into a message for Ava.
          </p>
          <p style={styles.p}>
            In the current online speech-to-text flow, AVAAIO receives
            an audio file, creates a temporary server file, and sends
            the audio to OpenAI’s Whisper transcription service. The
            temporary file created by the AVAAIO backend is deleted
            after the transcription request completes. The resulting
            transcript may then be processed like a typed message and
            may become part of conversation history or memory,
            depending on the user’s settings and use of the service.
          </p>
          <p style={styles.p}>
            To generate a spoken response, AVAAIO may send Ava’s
            response text to Microsoft Azure Speech. The backend may
            create a temporary synthesized audio file and delete that
            temporary file after delivering the audio. Ava’s voice is
            synthetic and computer-generated.
          </p>
          <p style={styles.p}>
            Third-party speech providers process submitted audio or
            text according to their applicable agreements, security
            practices, and data-handling terms. Users should not record
            another person without any consent required by applicable
            law.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Device Motion and Orientation</h2>
          <p style={styles.p}>
            When Device Motion is enabled and permission is granted,
            AVAAIO may read device-orientation values such as rotation
            and tilt so Ava can visually react to movement of the
            device.
          </p>
          <p style={styles.p}>
            Under the current implementation, raw orientation values
            are designed to be processed on the device for avatar
            animation. AVAAIO may contact its server to verify whether
            the signed-in account is entitled to use the feature.
            Device Motion is not intended for location tracking,
            physical surveillance, health measurement, or safety
            monitoring.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Messages, Learning, and Memory</h2>
          <p style={styles.p}>
            AVAAIO may store conversation turns and selected facts to
            maintain context, personalize Ava, support follow-up
            questions, improve reliability, and operate memory
            features. Stored information may include recent
            conversation history, profile memory, semantic memory,
            user-requested memories, and protected-memory entries.
          </p>
          <p style={styles.p}>
            A memory-learning control may limit some personalized
            learning, but disabling that control does not necessarily
            remove existing memories or prevent temporary processing,
            account records, fraud-prevention records, operational
            logs, or legally required retention.
          </p>
          <p style={styles.p}>
            Protected memory may use an additional memory code,
            time-limited unlock state, and email-based reset process.
            Users should not store passwords, payment information,
            government identifiers, medical records, or other highly
            sensitive information in Ava unless they fully understand
            and accept the risks.
          </p>
          <p style={styles.p}>
            Users may use available controls to clear profile memory,
            reset conversation context, correct information, or
            request deletion. Because AVAAIO uses more than one memory
            and operational storage system, removal requests may
            require processing across multiple records.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Authentication and Device Storage</h2>
          <p style={styles.p}>
            AVAAIO may use secure HTTP-only cookies and browser local
            or session storage to maintain authentication, account
            state, preferences, feature settings, pending ratings, and
            user-interface behavior. Browser-stored authentication
            information may remain until logout, expiration, deletion,
            or browser storage is cleared.
          </p>
          <p style={styles.p}>
            Users are responsible for protecting their devices,
            passwords, memory codes, and active sessions and for
            signing out of shared devices.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>How We Use Information</h2>
          <ul style={styles.list}>
            <li>
              To provide chat, voice, vision, motion, education,
              entertainment, account, subscription, support, memory,
              personalization, and security features.
            </li>
            <li>
              To authenticate users, verify account entitlements,
              enforce plan limits, process purchases, and prevent
              unauthorized access or fraud.
            </li>
            <li>
              To maintain conversation continuity, respond to user
              requests, diagnose failures, improve reliability, and
              repair platform issues.
            </li>
            <li>
              To respond to support, privacy, correction, access,
              deletion, and reactivation requests.
            </li>
            <li>
              To comply with legal obligations, enforce our Terms, and
              protect users, American Globalo, LLC, and the public.
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Service Providers</h2>
          <p style={styles.p}>
            AVAAIO may use service providers for AI processing,
            speech transcription, synthesized speech, cloud hosting,
            content delivery, authentication, email, security,
            payments, subscriptions, app distribution, and technical
            infrastructure.
          </p>
          <p style={styles.p}>
            Current or planned providers and technologies may include
            OpenAI, Microsoft Azure, Apple, Stripe, Google services,
            MediaPipe components, cloud-hosting providers, email
            providers, and content-delivery services. Providers may
            change as AVAAIO evolves. Information is shared with a
            provider only as reasonably necessary to operate the
            applicable feature, satisfy legal obligations, or protect
            the service.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Retention and Account Deletion</h2>
          <p style={styles.p}>
            Retention depends on the type of information and the
            feature involved. AVAAIO may retain account information,
            conversations, memory, support records, security records,
            billing metadata, and operational logs for as long as
            reasonably necessary to provide the service, protect the
            platform, resolve disputes, comply with law, and satisfy
            legitimate business requirements.
          </p>
          <p style={styles.p}>
            Temporary media files created by the AVAAIO backend for
            the current speech-processing flows are deleted after the
            request completes. This does not control the separate
            processing or retention practices of a third-party
            provider.
          </p>
          <p style={styles.p}>
            An account-deletion request may first deactivate the
            account and revoke access. The current account system may
            permit reactivation for up to 30 days. After the applicable
            recovery period, AVAAIO may delete or de-identify
            associated information, subject to technical limitations
            and records that must or may be retained for billing,
            fraud prevention, security, dispute resolution, legal
            compliance, or enforcement.
          </p>
          <p style={styles.p}>
            Users may submit a privacy or deletion request through
            available account controls or by contacting
            support@avaaio.com.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Security</h2>
          <p style={styles.p}>
            AVAAIO uses administrative, technical, and organizational
            safeguards intended to protect information, including
            authenticated endpoints, account isolation, role-based
            controls, secure cookies, password hashing, restricted
            administrator diagnostics, and encrypted network
            connections where supported.
          </p>
          <p style={styles.p}>
            No internet service, storage system, AI provider, or
            security control can guarantee absolute security. Users
            should avoid submitting information that is unnecessary
            for the requested feature.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Children and Minors</h2>
          <p style={styles.p}>
            AVAAIO is not directed to children under 13, and a child
            under 13 may not create or independently use an AVAAIO
            account. Do not submit personal information about a child
            under 13 unless AVAAIO expressly provides an appropriate
            parent-controlled and legally compliant process.
          </p>
          <p style={styles.p}>
            Users under the age of majority may use AVAAIO only with
            the permission and supervision of a parent or legal
            guardian who accepts responsibility for the minor’s use.
            A parent or guardian may contact support@avaaio.com
            regarding information submitted by a minor.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Privacy Choices and Rights</h2>
          <p style={styles.p}>
            Depending on where a user lives, applicable law may
            provide rights to request access, correction, deletion,
            restriction, or a copy of certain personal information.
            AVAAIO may need to verify identity before completing a
            request and may deny or limit a request when permitted by
            law.
          </p>
          <p style={styles.p}>
            Users may also manage camera, microphone, motion,
            notification, and browser permissions through their device
            or browser settings.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Changes to This Policy</h2>
          <p style={styles.p}>
            We may update this Privacy Policy as AVAAIO adds features,
            changes providers, modifies data practices, or responds to
            legal requirements. The updated date will appear at the
            top of this page. Material changes may also be announced
            through the platform or by email when appropriate.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Contact</h2>
          <p style={styles.p}>
            For privacy questions, correction requests, account
            deletion, or other data concerns, contact American
            Globalo, LLC at support@avaaio.com.
          </p>
        </section>

          <LegalBackToLogin />
        <nav style={styles.nav} aria-label="Legal navigation">
          <Link style={styles.link} to="/privacy-policy">
            Privacy Policy
          </Link>
          <Link style={styles.link} to="/terms-and-conditions">
            Terms & Conditions
          </Link>
          <Link style={styles.link} to="/ai-disclaimer">
            AI Disclaimer
          </Link>
          <Link style={styles.link} to="/credits">
            Credits & Acknowledgments
          </Link>
        </nav>

        <footer style={styles.footer}>
          © 2026 American Globalo, LLC. All rights reserved.
          <br />
          AVAAIO™ is owned and operated by American Globalo, LLC.
          Ava is the AI assistant experience within the AVAAIO™
          platform.
        </footer>
      </article>
    </main>
  );
}
